import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Modal } from '../../components/ui/Modal';
import { RichTextEditor } from '../../components/ui/RichTextEditor';
import { useAdminLayoutContext } from '../../components/admin/AdminLayout';
import {
  Plus,
  Edit2,
  Trash2,
  Target,
  CheckCircle,
  FileText,
  Hash,
  Clock,
} from 'lucide-react';

type ContentStatus = 'draft' | 'pending' | 'published';

interface ApproachWithCount {
  id: string;
  created_at: string;
  company_id?: string;
  number: number;
  name_no: string;
  name_en?: string;
  description?: string;
  rationale?: string;
  summary_file_url?: string;
  status: ContentStatus;
  version?: number;
  created_by?: string;
  approved_by?: string;
  influence_rule_count: number;
}

export function ApproachManagement() {
  const { profile, hasRole } = useAuth();
  const { setHeaderAction, setPageTitle } = useAdminLayoutContext();
  const [showModal, setShowModal] = useState(false);
  const [editingApproach, setEditingApproach] = useState<ApproachWithCount | null>(null);
  const [filterStatus, setFilterStatus] = useState<ContentStatus | 'all'>('all');
  const [approaches, setApproaches] = useState<ApproachWithCount[] | undefined>(undefined);
  const [formData, setFormData] = useState({
    number: 1,
    name_no: '',
    description: '',
    status: 'draft' as ContentStatus,
  });

  const canEdit = hasRole(['superadmin', 'org_admin', 'editor']);

  // Fetch approaches with influence rule counts
  const fetchApproaches = useCallback(async () => {
    let query = supabase
      .from('approaches')
      .select('*, influence_rules(count)')
      .order('number', { ascending: true });

    if (filterStatus !== 'all') {
      query = query.eq('status', filterStatus);
    }

    const { data, error } = await query;
    if (error) {
      console.error('Error fetching approaches:', error);
      return;
    }

    const mapped = (data || []).map((a: any) => ({
      ...a,
      influence_rule_count: a.influence_rules?.[0]?.count ?? 0,
    }));
    setApproaches(mapped);
  }, [filterStatus]);

  useEffect(() => {
    fetchApproaches();
  }, [fetchApproaches]);

  // Set page title and header action
  useEffect(() => {
    setPageTitle('Tilnærminger');

    if (canEdit) {
      setHeaderAction(
        <button
          onClick={() => {
            setEditingApproach(null);
            setFormData({
              number: (approaches?.length || 0) + 1,
              name_no: '',
              description: '',
              status: 'draft',
            });
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
        >
          <Plus className="h-5 w-5" />
          Ny tilnærming
        </button>
      );
    }

    return () => {
      setPageTitle(null);
      setHeaderAction(null);
    };
  }, [setHeaderAction, setPageTitle, canEdit, approaches?.length]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingApproach) {
        const { error } = await supabase
          .from('approaches')
          .update({
            number: formData.number,
            name_no: formData.name_no,
            description: formData.description || null,
          })
          .eq('id', editingApproach.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('approaches')
          .insert({
            company_id: profile?.companyId || null,
            number: formData.number,
            name_no: formData.name_no,
            description: formData.description || null,
            status: formData.status,
          });

        if (error) throw error;
      }

      setShowModal(false);
      setEditingApproach(null);
      resetForm();
      fetchApproaches();
    } catch (error: any) {
      console.error('Error saving approach:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleStatusChange = async (id: string, newStatus: ContentStatus) => {
    try {
      const { error } = await supabase
        .from('approaches')
        .update({ status: newStatus })
        .eq('id', id);

      if (error) throw error;
      fetchApproaches();
    } catch (error: any) {
      console.error('Error updating status:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleEdit = (approach: ApproachWithCount) => {
    setEditingApproach(approach);
    setFormData({
      number: approach.number,
      name_no: approach.name_no,
      description: approach.description || '',
      status: approach.status === 'pending' ? 'draft' : approach.status,
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Er du sikker? Dette vil også fjerne alle symptom-modul koblinger.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('approaches')
        .delete()
        .eq('id', id);

      if (error) throw error;
      fetchApproaches();
    } catch (error: any) {
      console.error('Error deleting approach:', error);
      alert(`Feil: ${error.message}`);
    }
  };

  const resetForm = () => {
    setFormData({
      number: (approaches?.length || 0) + 1,
      name_no: '',
      description: '',
      status: 'draft',
    });
  };

  if (approaches === undefined) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600 dark:text-gray-400">Laster tilnærminger...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filter */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-4">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Status:</span>
          <div className="flex gap-2">
            {(['all', 'draft', 'published'] as const).map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-3 py-1 rounded-md text-sm font-medium ${
                  filterStatus === status
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {status === 'all' ? 'Alle' : status === 'draft' ? 'Utkast' : 'Publisert'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Approaches table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {approaches.length === 0 ? (
          <div className="text-center py-12">
            <Target className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">Ingen tilnærminger funnet</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              {canEdit ? 'Opprett din første tilnærming' : 'Ingen tilnærminger matcher filteret'}
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Tilnærming
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Koblinger
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Versjon
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Handlinger
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {approaches.map((approach) => (
                <tr key={approach.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="font-medium text-gray-900 dark:text-white">{approach.number}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg flex items-center justify-center bg-purple-100 dark:bg-purple-900/50">
                        <Target className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">{approach.name_no}</div>
                        {approach.description && (
                          <div className="text-xs text-gray-400 dark:text-gray-500 mt-1 max-w-md line-clamp-1">
                            {approach.description.replace(/<[^>]*>/g, ' ').trim().substring(0, 100)}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {approach.influence_rule_count} symptomkoblinger
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {approach.status === 'published' ? (
                      <span
                        onClick={() => canEdit && handleStatusChange(approach.id, 'draft')}
                        className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-md ${canEdit ? 'cursor-pointer hover:opacity-80' : ''}`}
                        title={canEdit ? 'Klikk for å avpublisere' : undefined}
                      >
                        <CheckCircle className="h-3 w-3" />
                        Publisert
                      </span>
                    ) : (
                      <span
                        onClick={() => canEdit && handleStatusChange(approach.id, 'published')}
                        className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-orange-100 text-orange-700 rounded-md ${canEdit ? 'cursor-pointer hover:opacity-80' : ''}`}
                        title={canEdit ? 'Klikk for å publisere' : undefined}
                      >
                        <Clock className="h-3 w-3" />
                        Utkast
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600 dark:text-gray-400">v{approach.version || 1}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-2">
                      {canEdit && (
                        <>
                          <button
                            onClick={() => handleEdit(approach)}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-500 dark:hover:text-blue-400"
                            title="Rediger"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(approach.id)}
                            className="text-red-600 hover:text-red-900 dark:text-red-500 dark:hover:text-red-400"
                            title="Slett"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingApproach(null);
          resetForm();
        }}
        title={editingApproach ? 'Rediger tilnærming' : 'Ny tilnærming'}
        size="xl"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="number" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Nummer *
              </label>
              <input
                id="number"
                type="number"
                min="1"
                required
                value={formData.number}
                onChange={(e) => setFormData({ ...formData, number: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label htmlFor="name_no" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Navn *
              </label>
              <input
                id="name_no"
                type="text"
                required
                value={formData.name_no}
                onChange={(e) => setFormData({ ...formData, name_no: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Beskrivelse
            </label>
            <RichTextEditor
              value={formData.description}
              onChange={(value) => setFormData({ ...formData, description: value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Status *
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="status"
                  value="draft"
                  checked={formData.status === 'draft'}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as ContentStatus })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="text-gray-700 dark:text-gray-300">Utkast</span>
                <span className="text-xs text-gray-500">(kun synlig for admin)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="status"
                  value="published"
                  checked={formData.status === 'published'}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as ContentStatus })}
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300"
                />
                <span className="text-gray-700 dark:text-gray-300">Publisert</span>
                <span className="text-xs text-gray-500">(synlig for alle)</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setShowModal(false);
                setEditingApproach(null);
                resetForm();
              }}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Avbryt
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600"
            >
              {editingApproach ? 'Oppdater' : 'Opprett'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
